import {
  Component,
  ContentChild,
  ContentChildren,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { RouterService } from '@app/routing/router.service';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { Identifier } from '@app/utils/common';
import { StringMap } from '@app/utils/string-map';
import { BootstrapViewport } from '@app/utils/viewport';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-main-nav-item',
  templateUrl: './main-nav-item.component.html',
  standalone: false,
})
export class MainNavItemComponent implements OnChanges, OnInit {
  @Input()
  highlight: boolean = true;

  @Input()
  icon!: string;

  @Input()
  route!: string;

  @Input()
  routeParams?: StringMap<Identifier>;

  /**
   * If true then the popover is always shown when hovering over the item.
   * Otherwise, this value is ignored.
   */
  @Input()
  showPopover?: boolean;

  @ContentChild(TemplateRef)
  readonly popoverTemplate?: TemplateRef<any>;

  @ContentChildren(TranscludeDirective)
  readonly transclusions: QueryList<TranscludeDirective>;

  @ViewChild('tippy', { read: TippyDirective })
  readonly tippy!: TippyDirective;

  disableTippy$!: Observable<boolean>;
  showPopover$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authenticationService: AuthenticationService,
    private routerService: RouterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('showPopover' in changes) {
      this.showPopover$.next(changes.showPopover.currentValue);
    }
  }

  ngOnInit() {
    const isActive$ = combineLatest([this.showPopover$, this.routerService.routeDataChange$]).pipe(
      map(([showPopover]) =>
        this.menuItemIsActive(showPopover, this.authenticationService.isAuthenticated, this.route)
      )
    );

    const isMediumOrSmaller$ = BootstrapViewport.size$.pipe(map((size) => ['xs', 'sm', 'md'].includes(size)));

    this.disableTippy$ = combineLatest([isActive$, isMediumOrSmaller$]).pipe(
      map(([isActive, isMediumOrSmaller]) => isActive || isMediumOrSmaller),
      untilDestroyed(this)
    );
  }

  private menuItemIsActive(showPopover: boolean, isAuthenticated: boolean, route: string): boolean {
    return isAuthenticated && showPopover !== true && this.routerService.currentRouteMatches(route);
  }
}
